import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaUpload } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import AdminNav from '../../components/AdminNav';
import api from '../../utils/api';
import './AdminCategories.css';

const AdminCategories = () => {
  const { user } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', image: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      
      // Add file if selected, otherwise add existing image URL
      if (selectedFile) {
        formDataToSend.append('image', selectedFile);
      } else if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      if (editingId) {
        await api.put(`/categories/${editingId}`, formDataToSend, config);
      } else {
        await api.post('/categories', formDataToSend, config);
      }
      
      fetchCategories();
      resetForm();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.message || 'Failed to save category');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (5MB max for category images)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5MB limit.');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const removeFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleEdit = (category) => {
    setFormData({ 
      name: category.name, 
      description: category.description,
      image: category.image || ''
    });
    setEditingId(category._id);
    setShowForm(true);
    setPreviewUrl(category.image || '');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await api.delete(`/categories/${id}`);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', image: '' });
    setEditingId(null);
    setShowForm(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
  };

  if (!user || !user.isAdmin) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return <div className="admin-loading">Loading categories...</div>;
  }

  return (
    <>
      <AdminNav />
      <div className="admin-categories">
        <div className="admin-header">
        <div>
          <h1>Manage Categories</h1>
          <p>{categories.length} total categories</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <FaPlus /> Add Category
          </button>
        )}
      </div>

      {showForm && (
        <div className="category-form-card">
          <div className="form-header">
            <h2>{editingId ? 'Edit Category' : 'New Category'}</h2>
            <button onClick={resetForm} className="btn-close">
              <FaTimes />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Category Name *</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Living Room, Bedroom"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder="Brief description of the category"
              />
            </div>

            <div className="form-group">
              <label>Category Image</label>
              
              {/* Preview existing or selected image */}
              {(previewUrl || formData.image) && !selectedFile && (
                <div className="image-preview-container">
                  <img 
                    src={previewUrl || formData.image} 
                    alt="Category preview" 
                    className="category-image-preview"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, image: '' });
                      setPreviewUrl('');
                    }}
                    className="btn-remove-preview"
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              )}

              {/* Preview selected file */}
              {selectedFile && previewUrl && (
                <div className="image-preview-container">
                  <img 
                    src={previewUrl} 
                    alt="Selected file preview" 
                    className="category-image-preview"
                  />
                  <div className="file-info">
                    <p className="file-name">{selectedFile.name}</p>
                    <p className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="btn-remove-preview"
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              )}

              {/* File upload button */}
              <div className="file-upload-section">
                <input
                  type="file"
                  id="category-image-upload"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                <label htmlFor="category-image-upload" className="file-upload-label">
                  <FaUpload /> {selectedFile || previewUrl ? 'Change Image' : 'Upload Image'}
                </label>
                <p className="file-hint">Max 5MB. Supports: JPEG, PNG, GIF, WebP</p>
              </div>

              {/* Option to use URL instead */}
              {!selectedFile && (
                <div className="url-input-section">
                  <label htmlFor="imageUrl">Or enter image URL</label>
                  <input
                    type="url"
                    id="imageUrl"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={uploading} className="btn btn-primary">
                <FaSave /> {uploading ? 'Saving...' : (editingId ? 'Update' : 'Create')} Category
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="categories-grid">
        {categories.map(category => (
          <div key={category._id} className="category-card">
            {category.image && (
              <div className="category-image">
                <img src={category.image} alt={category.name} />
              </div>
            )}
            <div className="category-content">
              <h3>{category.name}</h3>
              <p>{category.description || 'No description'}</p>
            </div>
            <div className="category-actions">
              <button onClick={() => handleEdit(category)} className="btn-icon btn-edit">
                <FaEdit />
              </button>
              <button onClick={() => handleDelete(category._id)} className="btn-icon btn-delete">
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="no-categories">
          <p>No categories yet. Create your first category!</p>
        </div>
      )}
    </div>
    </>
  );
};

export default AdminCategories;
